/**
 * ManagerPerformanceDashboard.js
 * Modal for displaying manager performance bar charts using react-chartjs-2 and Chart.js.
 * Adheres strictly to .clinerules and .clineinstructions.
 */

import React, { useMemo, useState, useRef, useCallback } from "react";
import { Bar } from "react-chartjs-2";
import { Chart, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from "chart.js";
import { getCurrentYear, calculateYearSpecificTotals } from "../../utils/yearSpecificUtils";
import PropTypes from "prop-types";
import { getTierColor } from "../../services/TierColorService";
import "../../styles/components/dialogs/ManagerPerformanceDashboard.css";
// For Excel export
import { useState as useReactState } from "react";

// Helper to blend two hex colors (50/50)
function blendHexColors(hex1, hex2, ratio = 0.5) {
  // Remove # if present
  hex1 = hex1.replace(/^#/, "");
  hex2 = hex2.replace(/^#/, "");
  // Parse r,g,b
  const r1 = parseInt(hex1.substring(0,2),16), g1 = parseInt(hex1.substring(2,4),16), b1 = parseInt(hex1.substring(4,6),16);
  const r2 = parseInt(hex2.substring(0,2),16), g2 = parseInt(hex2.substring(2,4),16), b2 = parseInt(hex2.substring(4,6),16);
  // Blend
  const r = Math.round(r1 * (1 - ratio) + r2 * ratio);
  const g = Math.round(g1 * (1 - ratio) + g2 * ratio);
  const b = Math.round(b1 * (1 - ratio) + b2 * ratio);
  return "#" + [r,g,b].map(x => x.toString(16).padStart(2,"0")).join("");
}

Chart.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const MODAL_STYLE = {
  position: "fixed",
  top: "2%",
  left: "50%",
  transform: "translateX(-50%)",
  width: "98vw",
  maxWidth: "1800px",
  minHeight: "800px",
  background: "#fff",
  borderRadius: "12px",
  boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
  zIndex: 3000,
  padding: "12px 40px 24px 40px",
  overflowY: "auto",
  display: "flex",
  flexDirection: "column"
};

const HEADER_STYLE = {
  fontSize: "2rem",
  fontWeight: 700,
  color: "#0074D9",
  marginBottom: "24px",
  textAlign: "center"
};

const TOGGLE_STYLE = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "24px",
  marginBottom: "24px"
};

const CLOSE_BTN_STYLE = {
  position: "absolute",
  top: 18,
  right: 24,
  fontSize: "2rem",
  background: "none",
  border: "none",
  color: "#888",
  cursor: "pointer"
};

function getAllManagers(clientData) {
  const managers = new Set();
  clientData.forEach(client => {
    let mgr = client.conference_manager || client["Assigned to"] || "Unassigned";
    if (!mgr || mgr.trim() === "") mgr = "Unassigned";
    managers.add(mgr);
  });
  return Array.from(managers).sort();
}

function getAvailableYears(clientData) {
  const yearsSet = new Set();
  clientData.forEach(client => {
    Object.keys(client).forEach(key => {
      const match = key.match(/^(\d{4}) Revenue$/);
      if (match) yearsSet.add(match[1]);
    });
  });
  return Array.from(yearsSet).sort();
}

function getManagerMetrics(clientData, managers, year) {
  // For each manager, get totals for rooms, RN, revenue, client count for the selected year
  return managers.map(manager => {
    const totals = calculateYearSpecificTotals(clientData, manager, year);
    // Count unique clients for this manager and year
    const clients = clientData.filter(
      c => (c.conference_manager || c["Assigned to"] || "Unassigned") === manager &&
        (c[`${year} Rooms`] > 0 || c[`${year} RN`] > 0 || (typeof c[`${year} Revenue`] === "string" && parseFloat(c[`${year} Revenue`].replace(/[$,]/g, "")) > 0))
    );
    return {
      manager,
      rooms: totals.rooms,
      rn: totals.rn,
      revenue: parseFloat(totals.revenue.toString().replace(/[$,]/g, "")) || 0,
      clientCount: clients.length
    };
  });
}

function getPercentageMetrics(metrics) {
  // For each metric, calculate total and then percentage per manager
  const totals = {
    rooms: metrics.reduce((sum, m) => sum + m.rooms, 0),
    rn: metrics.reduce((sum, m) => sum + m.rn, 0),
    revenue: metrics.reduce((sum, m) => sum + m.revenue, 0),
    clientCount: metrics.reduce((sum, m) => sum + m.clientCount, 0)
  };
  return metrics.map(m => ({
    manager: m.manager,
    rooms: totals.rooms ? (m.rooms / totals.rooms) * 100 : 0,
    rn: totals.rn ? (m.rn / totals.rn) * 100 : 0,
    revenue: totals.revenue ? (m.revenue / totals.revenue) * 100 : 0,
    clientCount: totals.clientCount ? (m.clientCount / totals.clientCount) * 100 : 0
  }));
}

export default function ManagerPerformanceDashboard({ clientData, onClose }) {
  // Chart reference to prevent re-rendering
  const chartRef = useRef(null);
  
  // Pre-load XLSX library to avoid dynamic import delay
  const xlsxLibRef = useRef(null);
  
  // Memoize formatCurrency to prevent re-creation
  const formatCurrency = useCallback((num) => {
    if (typeof num !== "number" || isNaN(num)) return "$0";
    return "$" + num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }, []);
  
  const availableYears = useMemo(() => getAvailableYears(clientData), [clientData]);
  // Determine current and last year: current year is always the actual current year (from system date)
  const systemCurrentYear = getCurrentYear();
  const currentYear = systemCurrentYear;
  const lastYear = (parseInt(systemCurrentYear, 10) - 1).toString();

  const [yearMode, setYearMode] = useState("current"); // "current", "last", "both"
  const [showTierBreakdown, setShowTierBreakdown] = useState(false); // For the third view

  const managers = useMemo(() => getAllManagers(clientData), [clientData]);

  // Prepare metrics for each year
  const metricsCurrent = useMemo(
    () => getManagerMetrics(clientData, managers, currentYear),
    [clientData, managers, currentYear]
  );
  const metricsLast = useMemo(
    () => getManagerMetrics(clientData, managers, lastYear),
    [clientData, managers, lastYear]
  );

  const percentMetricsCurrent = useMemo(() => getPercentageMetrics(metricsCurrent), [metricsCurrent]);
  const percentMetricsLast = useMemo(() => getPercentageMetrics(metricsLast), [metricsLast]);

  // --- Tier breakdown calculation ---
  // For each manager, count total clients and clients by tier
  // Also, for each manager, calculate revenue by tier
  const tierBreakdown = useMemo(() => {
    // Define tier keys and colors
    const tierKeys = [
      { key: "VIP", color: blendHexColors(getTierColor("VIP", "dark"), getTierColor("VIP", "light"), 0.3) },
      { key: "T1", color: blendHexColors(getTierColor("1", "dark"), getTierColor("1", "light"), 0.3) },
      { key: "T2", color: blendHexColors(getTierColor("2", "dark"), getTierColor("2", "light"), 0.3) },
      { key: "T3", color: blendHexColors(getTierColor("3", "dark"), getTierColor("3", "light"), 0.3) },
      { key: "N/A", color: blendHexColors("#adb5bd", getTierColor("N/A", "light"), 0.3) }
    ];
    // Get all clients for each manager
    const managerClientMap = {};
    managers.forEach(manager => {
      managerClientMap[manager] = clientData.filter(
        c => (c.conference_manager || c["Assigned to"] || "Unassigned") === manager
      );
    });
    // For each manager, count clients by tier, revenue by tier, and RN by tier
    const result = {};
    let totalClients = 0;
    let totalRevenue = 0;
    let totalRN = 0;
    let totalRooms = 0;
    const managerTotalRevenue = {};
    const managerTotalRN = {};
    const managerTotalRooms = {};
    managers.forEach(manager => {
      const clients = managerClientMap[manager];
      totalClients += clients.length;
      // Sum revenue for this manager (all years)
      let managerRevenue = 0;
      clients.forEach(c => {
        let rev = 0;
        if (c["Total Revenue"]) {
          rev = parseFloat((c["Total Revenue"] + "").replace(/[$,]/g, "")) || 0;
        }
        managerRevenue += rev;
      });
      managerTotalRevenue[manager] = managerRevenue;
      totalRevenue += managerRevenue;
      // Sum RN for this manager for the current year
      let managerRN = 0;
      const rnField = `${currentYear} RN`;
      clients.forEach(c => {
        let rn = 0;
        if (c[rnField] !== undefined && c[rnField] !== null && c[rnField] !== "") {
          rn = parseFloat((c[rnField] + "").replace(/[$,]/g, "")) || 0;
        }
        managerRN += rn;
      });
      managerTotalRN[manager] = managerRN;
      totalRN += managerRN;
      // Sum Rooms for this manager for the current year
      let managerRooms = 0;
      const roomsField = `${currentYear} Rooms`;
      clients.forEach(c => {
        let rooms = 0;
        if (c[roomsField] !== undefined && c[roomsField] !== null && c[roomsField] !== "") {
          rooms = parseFloat((c[roomsField] + "").replace(/[$,]/g, "")) || 0;
        }
        managerRooms += rooms;
      });
      managerTotalRooms[manager] = managerRooms;
      totalRooms += managerRooms;
    });
    managers.forEach(manager => {
      const clients = managerClientMap[manager];
      const totalCount = clients.length;
      // Count by tier
      const tierCounts = {
        VIP: 0,
        T1: 0,
        T2: 0,
        T3: 0,
        "N/A": 0
      };
      // Revenue by tier
      const tierRevenue = {
        VIP: 0,
        T1: 0,
        T2: 0,
        T3: 0,
        "N/A": 0
      };
      // RN by tier
      const tierRN = {
        VIP: 0,
        T1: 0,
        T2: 0,
        T3: 0,
        "N/A": 0
      };
      // Rooms by tier
      const tierRooms = {
        VIP: 0,
        T1: 0,
        T2: 0,
        T3: 0,
        "N/A": 0
      };
      clients.forEach(c => {
        let tier = (c["Tier"] || "").toString().toUpperCase().replace(/\s/g, "");
        let rev = 0;
        if (c["Total Revenue"]) {
          rev = parseFloat((c["Total Revenue"] + "").replace(/[$,]/g, "")) || 0;
        }
        // Use current year RN field, e.g., "2025 RN"
        let rn = 0;
        const rnField = `${currentYear} RN`;
        if (c[rnField] !== undefined && c[rnField] !== null && c[rnField] !== "") {
          rn = parseFloat((c[rnField] + "").replace(/[$,]/g, "")) || 0;
        }
        // Use current year Rooms field, e.g., "2025 Rooms"
        let rooms = 0;
        const roomsField = `${currentYear} Rooms`;
        if (c[roomsField] !== undefined && c[roomsField] !== null && c[roomsField] !== "") {
          rooms = parseFloat((c[roomsField] + "").replace(/[$,]/g, "")) || 0;
        }
        if (tier === "VIP") { tierCounts.VIP++; tierRevenue.VIP += rev; tierRN.VIP += rn; tierRooms.VIP += rooms; }
        else if (tier === "T1" || tier === "1") { tierCounts.T1++; tierRevenue.T1 += rev; tierRN.T1 += rn; tierRooms.T1 += rooms; }
        else if (tier === "T2" || tier === "2") { tierCounts.T2++; tierRevenue.T2 += rev; tierRN.T2 += rn; tierRooms.T2 += rooms; }
        else if (tier === "T3" || tier === "3") { tierCounts.T3++; tierRevenue.T3 += rev; tierRN.T3 += rn; tierRooms.T3 += rooms; }
        else { tierCounts["N/A"]++; tierRevenue["N/A"] += rev; tierRN["N/A"] += rn; tierRooms["N/A"] += rooms; }
      });
      // Debug output for abrkic
      if (manager === "abrkic") {
        // managerRN is calculated below, so recalculate here for debug
        let managerRN = 0;
        clients.forEach(c => {
          const rnField = `${currentYear} RN`;
          let rn = 0;
          if (c[rnField] !== undefined && c[rnField] !== null && c[rnField] !== "") {
            rn = parseFloat((c[rnField] + "").replace(/[$,]/g, "")) || 0;
          }
          managerRN += rn;
        });
        // Print debug info
        // eslint-disable-next-line no-console
        console.log("Manager: abrkic");
        // eslint-disable-next-line no-console
        console.log("currentYear:", currentYear);
        clients.forEach(c => {
          // eslint-disable-next-line no-console
          console.log("Client:", c.client_name, `${currentYear} RN:`, c[`${currentYear} RN`], "Tier:", c["Tier"]);
        });
        // eslint-disable-next-line no-console
        console.log("managerRN:", managerRN);
        // eslint-disable-next-line no-console
        console.log("tierRN:", tierRN);
      }
      // a) % of all clients assigned to manager
      const pctAllClients = totalClients > 0 ? (totalCount / totalClients) * 100 : 0;
      // b) % of each tier among manager's clients
      const pctOfManager = {};
      tierKeys.forEach(t => {
        pctOfManager[t.key] = totalCount > 0 ? (tierCounts[t.key] / totalCount) * 100 : 0;
      });
      // c) Each segment percent (relative to total bar height) for client count
      const segmentPct = {};
      tierKeys.forEach(t => {
        segmentPct[t.key] = (pctAllClients * pctOfManager[t.key]) / 100;
      });
      // d) Revenue breakdown
      const managerRevenue = managerTotalRevenue[manager];
      const pctAllRevenue = totalRevenue > 0 ? (managerRevenue / totalRevenue) * 100 : 0;
      const pctOfManagerRevenue = {};
      tierKeys.forEach(t => {
        pctOfManagerRevenue[t.key] = managerRevenue > 0 ? (tierRevenue[t.key] / managerRevenue) * 100 : 0;
      });
      const segmentRevenuePct = {};
      tierKeys.forEach(t => {
        segmentRevenuePct[t.key] = (pctAllRevenue * pctOfManagerRevenue[t.key]) / 100;
      });
      // e) RN breakdown
      const managerRN = managerTotalRN[manager];
      const pctAllRN = totalRN > 0 ? (managerRN / totalRN) * 100 : 0;
      const pctOfManagerRN = {};
      tierKeys.forEach(t => {
        pctOfManagerRN[t.key] = managerRN > 0 ? (tierRN[t.key] / managerRN) * 100 : 0;
      });
      const segmentRNPct = {};
      tierKeys.forEach(t => {
        segmentRNPct[t.key] = (pctAllRN * pctOfManagerRN[t.key]) / 100;
      });
      // Debug output for abrkic: segmentRNPct
      if (manager === "abrkic") {
        // eslint-disable-next-line no-console
        console.log("abrkic managerTotalRN:", managerTotalRN[manager]);
        // eslint-disable-next-line no-console
        console.log("abrkic totalRN (sum of all managerTotalRN):", Object.values(managerTotalRN).reduce((a, b) => a + b, 0));
        // eslint-disable-next-line no-console
        console.log("abrkic segmentRNPct:", segmentRNPct);
      }
      // f) Rooms breakdown
      const managerRooms = managerTotalRooms[manager];
      const pctAllRooms = totalRooms > 0 ? (managerRooms / totalRooms) * 100 : 0;
      const pctOfManagerRooms = {};
      tierKeys.forEach(t => {
        pctOfManagerRooms[t.key] = managerRooms > 0 ? (tierRooms[t.key] / managerRooms) * 100 : 0;
      });
      const segmentRoomsPct = {};
      tierKeys.forEach(t => {
        segmentRoomsPct[t.key] = (pctAllRooms * pctOfManagerRooms[t.key]) / 100;
      });
      result[manager] = {
        totalCount,
        tierCounts,
        pctAllClients,
        pctOfManager,
        segmentPct,
        tierRevenue,
        managerRevenue,
        pctAllRevenue,
        pctOfManagerRevenue,
        segmentRevenuePct,
        tierRN,
        managerRN,
        pctAllRN,
        pctOfManagerRN,
        segmentRNPct,
        tierRooms,
        managerRooms,
        pctAllRooms,
        pctOfManagerRooms,
        segmentRoomsPct
      };
    });
    return { tierKeys, breakdown: result };
  }, [clientData, managers]);

  // Optimized Excel export logic with minimal re-renders
  const handleExportExcel = useCallback(async () => {
    try {
      // Dynamically import xlsx-js-style
      const XLSX = await import("xlsx-js-style");
      // Helper to build a styled sheet for a given metrics array
      function buildSheet(metrics, yearLabel, userName = "N/A") {
        // --- METADATA SECTION (matches pinned clients style) ---
        const now = new Date();
        const exportDate = now.toLocaleString();
        const title = "MANAGER PERFORMANCE DASHBOARD EXPORT";
        const description = "Includes manager-level totals for Rooms, Room Nights, Revenue, and Client Count.";
        // Title row (merged)
        const titleRow = [
          {
            v: title,
            t: "s",
            s: {
              font: { bold: true, sz: 16, color: { rgb: "0074D9" }, name: "Segoe UI" },
              fill: { fgColor: { rgb: "FFFFFF" } },
              alignment: { horizontal: "center", vertical: "center" }
            }
          },
          ...Array(8).fill({ v: "", t: "s", s: { fill: { fgColor: { rgb: "FFFFFF" } } } })
        ];
        // Second row for merging
        const titleRow2 = Array(9).fill({ v: "", t: "s", s: { fill: { fgColor: { rgb: "FFFFFF" } } } });
        // Spacing row
        const spacingRow = Array(9).fill({ v: "", t: "s", s: { fill: { fgColor: { rgb: "FFFFFF" } } } });
        // Info row 1: Only Report Generated and Exported By (columns A–D)
        const infoRow1 = [
          { v: "Report Generated:", t: "s", s: { font: { bold: true, sz: 10, name: "Segoe UI" }, alignment: { horizontal: "center" } } },
          { v: exportDate, t: "s", s: { font: { sz: 10, name: "Segoe UI" }, alignment: { horizontal: "center" } } },
          { v: "Exported By:", t: "s", s: { font: { bold: true, sz: 10, name: "Segoe UI" }, alignment: { horizontal: "center" } } },
          { v: userName, t: "s", s: { font: { sz: 10, name: "Segoe UI" }, alignment: { horizontal: "center" } } },
          ...Array(5).fill({ v: "", t: "s", s: { fill: { fgColor: { rgb: "FFFFFF" } } } })
        ];
        // Description row: merged far to the right, not overlapping value columns
        const descriptionRow = [
          ...Array(9).fill({ v: "", t: "s", s: { fill: { fgColor: { rgb: "FFFFFF" } } } }),
          { v: "Description:", t: "s", s: { font: { bold: true, sz: 10, name: "Segoe UI" }, alignment: { horizontal: "left" } } },
          { v: description, t: "s", s: { font: { sz: 10, name: "Segoe UI" }, alignment: { horizontal: "left", wrapText: true } } },
          ...Array(3).fill({ v: "", t: "s", s: { fill: { fgColor: { rgb: "FFFFFF" } } } })
        ];
        // Empty row separator
        const emptyRow = Array(9).fill({ v: "", t: "s", s: { fill: { fgColor: { rgb: "FFFFFF" } } } });

        // --- DATA SECTION ---
        // Remove all (%) from headers
        const headers = ["Manager", "Rooms", "Room Nights", "Revenue", "Client Count", "Rooms (Raw)", "Room Nights (Raw)", "Revenue (Raw)", "Client Count (Raw)"];
        const data = [
          headers,
          ...metrics.map(m => [
            m.manager,
            m.rooms,
            m.rn,
            m.revenue,
            m.clientCount,
            m.roomsRaw || "",
            m.rnRaw || "",
            m.revenueRaw ? formatCurrency(m.revenueRaw) : "",
            m.clientCountRaw || ""
          ])
        ];
        // Style: header row bold, blue, center; data rows normal
        const styledData = [
          titleRow,
          titleRow2,
          infoRow1,
          emptyRow,
          ...data.map((row, i) =>
            row.map((cell, j) => ({
              v: cell,
              t: typeof cell === "number" ? "n" : "s",
              s: i === 0
                ? {
                    font: { bold: true, color: { rgb: "0074D9" }, name: "Segoe UI" },
                    alignment: { horizontal: "center", vertical: "center" },
                    fill: { fgColor: { rgb: "F0F4F8" } }
                  }
                : {
                    font: { name: "Segoe UI" },
                    alignment: { horizontal: "center", vertical: "center" }
                  }
            }))
          )
        ];
        const ws = XLSX.utils.aoa_to_sheet(styledData.map(r => r.map(c => c.v)));
        // Apply styles
        styledData.forEach((row, i) => {
          row.forEach((cell, j) => {
            const ref = XLSX.utils.encode_cell({ r: i, c: j });
            ws[ref].s = cell.s;
          });
        });
        // Merge title row across all columns
        if (!ws["!merges"]) ws["!merges"] = [];
        ws["!merges"].push({ s: { r: 0, c: 0 }, e: { r: 1, c: 8 } });
        // Merge Purpose and Description fields far to the right (columns J–M)
        // Set all columns (A–N) to width 30
        ws["!cols"] = Array(14).fill({ wch: 30 });
        ws["!rows"] = styledData.map(() => ({ hpt: 22 }));
        ws["!autofilter"] = { ref: `A${styledData.length - data.length + 1}:I${styledData.length}` };
        return ws;
      }
      // Build metrics for each sheet
      // 1. Current Year
      const currentMetrics = metricsCurrent.map(m => ({
        ...m,
        roomsRaw: m.rooms,
        rnRaw: m.rn,
        revenueRaw: m.revenue,
        clientCountRaw: m.clientCount
      }));
      // 2. Last Year
      const lastMetrics = metricsLast.map(m => ({
        ...m,
        roomsRaw: m.rooms,
        rnRaw: m.rn,
        revenueRaw: m.revenue,
        clientCountRaw: m.clientCount
      }));
      // 3. Tier Breakdown (flattened for export)
      const tierKeys = tierBreakdown.tierKeys.map(t => t.key);
      const tierMetrics = Object.entries(tierBreakdown.breakdown).map(([manager, breakdown]) => ({
        manager,
        ...tierKeys.reduce((acc, tier) => {
          acc[`Clients ${tier}`] = breakdown.tierCounts[tier] || 0;
          acc[`Revenue ${tier}`] = breakdown.tierRevenue[tier] || 0;
          acc[`RN ${tier}`] = breakdown.tierRN[tier] || 0;
          acc[`Rooms ${tier}`] = breakdown.tierRooms[tier] || 0;
          return acc;
        }, {})
      }));
      // Build tier breakdown sheet
      const tierHeaders = ["Manager"];
      tierKeys.forEach(tier => {
        tierHeaders.push(`Clients ${tier}`, `Revenue ${tier}`, `RN ${tier}`, `Rooms ${tier}`);
      });
      const tierData = [
        tierHeaders,
        ...tierMetrics.map(row => [
          row.manager,
          ...tierKeys.flatMap(tier => [
            row[`Clients ${tier}`],
            row[`Revenue ${tier}`] ? formatCurrency(row[`Revenue ${tier}`]) : "",
            row[`RN ${tier}`],
            row[`Rooms ${tier}`]
          ])
        ])
      ];
      const styledTierData = tierData.map((row, i) =>
        row.map((cell, j) => ({
          v: cell,
          t: typeof cell === "number" ? "n" : "s",
          s: i === 0
            ? {
                font: { bold: true, color: { rgb: "0074D9" }, name: "Segoe UI" },
                alignment: { horizontal: "center", vertical: "center" },
                fill: { fgColor: { rgb: "F0F4F8" } }
              }
            : {
                font: { name: "Segoe UI" },
                alignment: { horizontal: "center", vertical: "center" }
              }
        }))
      );
        // --- METADATA SECTION for Tier Breakdown sheet ---
        const nowTier = new Date();
        const exportDateTier = nowTier.toLocaleString();
        const titleTier = "MANAGER PERFORMANCE DASHBOARD EXPORT";
        const purposeTier = "This report summarizes manager performance metrics for the selected year(s).";
        const descriptionTier = "Tier breakdown: clients, revenue, room nights, and rooms by manager and tier.";
        const titleRowTier = [
          {
            v: titleTier,
            t: "s",
            s: {
              font: { bold: true, sz: 16, color: { rgb: "0074D9" }, name: "Segoe UI" },
              fill: { fgColor: { rgb: "FFFFFF" } },
              alignment: { horizontal: "center", vertical: "center" }
            }
          },
          ...Array(tierHeaders.length - 1).fill({ v: "", t: "s", s: { fill: { fgColor: { rgb: "FFFFFF" } } } })
        ];
        const titleRow2Tier = Array(tierHeaders.length).fill({ v: "", t: "s", s: { fill: { fgColor: { rgb: "FFFFFF" } } } });
        const spacingRowTier = Array(tierHeaders.length).fill({ v: "", t: "s", s: { fill: { fgColor: { rgb: "FFFFFF" } } } });
        // Info row 1 for Tier Breakdown: Only Report Generated and Exported By (columns A–D)
        const infoRow1Tier = [
          { v: "Report Generated:", t: "s", s: { font: { bold: true, sz: 10, name: "Segoe UI" }, alignment: { horizontal: "center" } } },
          { v: exportDateTier, t: "s", s: { font: { sz: 10, name: "Segoe UI" }, alignment: { horizontal: "center" } } },
          { v: "Exported By:", t: "s", s: { font: { bold: true, sz: 10, name: "Segoe UI" }, alignment: { horizontal: "center" } } },
          { v: "N/A", t: "s", s: { font: { sz: 10, name: "Segoe UI" }, alignment: { horizontal: "center" } } },
          ...Array(tierHeaders.length - 4).fill({ v: "", t: "s", s: { fill: { fgColor: { rgb: "FFFFFF" } } } })
        ];
        const emptyRowTier = Array(tierHeaders.length).fill({ v: "", t: "s", s: { fill: { fgColor: { rgb: "FFFFFF" } } } });

        // Remove (%) from tier headers
        const cleanTierHeaders = tierHeaders.map(h => h.replace(/%/g, ""));
      // --- Tier color background logic for Excel export ---
      // Compute very pale tier color (blend light color with white, 80% white)
      // Blend two hex colors (e.g., light and dark) by ratio
      function blendHexColors(hex1, hex2, ratio = 0.5) {
        hex1 = hex1.replace(/^#/, "");
        hex2 = hex2.replace(/^#/, "");
        const r1 = parseInt(hex1.substring(0,2),16), g1 = parseInt(hex1.substring(2,4),16), b1 = parseInt(hex1.substring(4,6),16);
        const r2 = parseInt(hex2.substring(0,2),16), g2 = parseInt(hex2.substring(2,4),16), b2 = parseInt(hex2.substring(4,6),16);
        const r = Math.round(r1 * (1 - ratio) + r2 * ratio);
        const g = Math.round(g1 * (1 - ratio) + g2 * ratio);
        const b = Math.round(b1 * (1 - ratio) + b2 * ratio);
        return "#" + [r,g,b].map(x => x.toString(16).padStart(2,"0")).join("");
      }
      // Map: for each tier, get the 4 column indices (Clients, Revenue, RN, Rooms)
      const tierColMap = {};
      let colIdx = 1; // Start after "Manager"
      tierBreakdown.tierKeys.forEach(tier => {
        tierColMap[tier.key] = [colIdx, colIdx+1, colIdx+2, colIdx+3];
        colIdx += 4;
      });
      // Map: for each tier, get pale color
      const paleTierColors = {};
      tierBreakdown.tierKeys.forEach(tier => {
        // Make color even less intensive: blend 95% light color, 5% dark color
        const light = getTierColor(tier.key.replace(/^T/, ""), "light");
        const dark = getTierColor(tier.key.replace(/^T/, ""), "dark");
        paleTierColors[tier.key] = blendHexColors(light, dark, 0.05);
      });

      const styledTierDataWithMeta = [
        titleRowTier,
        titleRow2Tier,
        infoRow1Tier,
        emptyRowTier,
        ...tierData.map((row, i) =>
          row.map((cell, j) => {
            // For data rows (not header), replace empty with dash
            const isHeader = i === 0;
            const value = (!isHeader && (cell === "" || cell === null || cell === undefined)) ? "-" : cell;
            // Determine if this column is a tier column
            let tierBg = null;
            if (!isHeader && j > 0) {
              // Find which tier this column belongs to
              for (const tier of tierBreakdown.tierKeys) {
                if (tierColMap[tier.key].includes(j)) {
                  tierBg = paleTierColors[tier.key];
                  break;
                }
              }
            }
            // Always apply thin border on all sides, with Excel default grid color
            const borderColor = { rgb: "D4D4D4" };
            const border = {
              top: { style: "thin", color: borderColor },
              left: { style: "thin", color: borderColor },
              right: { style: "thin", color: borderColor },
              bottom: { style: "thin", color: borderColor }
            };
            // Use light blue for all header cells
            const headerFill = { fgColor: { rgb: "F0F4F8" } };
            return {
              v: value,
              t: typeof value === "number" ? "n" : "s",
              s: isHeader
                ? {
                    font: { bold: true, color: { rgb: "0074D9" }, name: "Segoe UI" },
                    alignment: { horizontal: "center", vertical: "center" },
                    fill: headerFill,
                    border
                  }
                : {
                    font: { name: "Segoe UI" },
                    alignment: { horizontal: "center", vertical: "center" },
                    fill: tierBg ? { fgColor: { rgb: tierBg.replace(/^#/, "") } } : undefined,
                    border
                  }
            };
          })
        )
      ];

        const wsTier = XLSX.utils.aoa_to_sheet(styledTierDataWithMeta.map(r => r.map(c => c.v)));
        styledTierDataWithMeta.forEach((row, i) => {
          row.forEach((cell, j) => {
            const ref = XLSX.utils.encode_cell({ r: i, c: j });
            wsTier[ref].s = cell.s;
          });
        });
        // Merge title row across all columns (center the report title in top two rows)
        if (!wsTier["!merges"]) wsTier["!merges"] = [];
        wsTier["!merges"].push({ s: { r: 0, c: 0 }, e: { r: 1, c: tierHeaders.length - 1 } });
        wsTier["!cols"] = cleanTierHeaders.map(() => ({ wch: 18 }));
        wsTier["!rows"] = styledTierDataWithMeta.map(() => ({ hpt: 22 }));
        wsTier["!autofilter"] = { ref: `A${styledTierDataWithMeta.length - tierData.length + 1}:${String.fromCharCode(65 + tierHeaders.length - 1)}${styledTierDataWithMeta.length}` };
      // Create workbook and append sheets
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, buildSheet(currentMetrics, "Current Year"), "Current Year");
      XLSX.utils.book_append_sheet(wb, buildSheet(lastMetrics, "Last Year"), "Last Year");
      XLSX.utils.book_append_sheet(wb, wsTier, "Tier Breakdown");
      // Filename
      const now = new Date();
      const timestamp = now.toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const filename = `Manager Performances_${timestamp}.xlsx`;
      // Write file
      XLSX.writeFile(wb, filename, { compression: true, bookType: "xlsx" });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Excel export error:", err);
    }
  }, [metricsCurrent, metricsLast, tierBreakdown, formatCurrency]);

  // Chart.js datasets
  const barLabels = managers;
  // Gradients: light gray to blue, darkest blue is #0074D9 (title color)
  const barColors = [
    "rgba(220, 225, 235, 1)",   // very light gray-blue
    "rgba(180, 200, 230, 1)",   // light blue-gray
    "rgba(100, 150, 220, 1)",   // medium blue
    "#0074D9"                   // darkest blue (title color)
  ];
  const orangeColor = "#FF9800";

  // Each manager group: 4 bars (rooms, rn, revenue, clientCount)
function buildDatasets(percentMetrics, labelSuffix, forceOrange = false) {
    // For each metric, build a dataset with one bar per manager
    if (forceOrange) {
      // For each metric, create two datasets: bottom (orange, labeled), top (green, unlabeled)
      // For Client Count, create a dataset for each tier
      const { tierKeys, breakdown } = tierBreakdown;
      const datasets = [
        // Rooms: one dataset per tier, stacked (these will appear in the legend)
        ...tierKeys.map(tier => ({
          label: tier.key,
          data: managers.map(manager => breakdown[manager] ? breakdown[manager].segmentRoomsPct[tier.key] : 0),
          backgroundColor: tier.color,
          stack: "Rooms"
        })),
        // Room Nights: one dataset per tier, stacked (no legend label)
        ...tierKeys.map(tier => ({
          label: "",
          data: managers.map(manager => breakdown[manager] ? breakdown[manager].segmentRNPct[tier.key] : 0),
          backgroundColor: tier.color,
          stack: "Room Nights"
        })),
        // Revenue: one dataset per tier, stacked (no legend label)
        ...tierKeys.map(tier => ({
          label: "",
          data: managers.map(manager => breakdown[manager] ? breakdown[manager].segmentRevenuePct[tier.key] : 0),
          backgroundColor: tier.color,
          stack: "Revenue"
        })),
        // Client Count: one dataset per tier, stacked (no legend label)
        ...tierKeys.map(tier => ({
          label: "",
          data: managers.map(manager => breakdown[manager] ? breakdown[manager].segmentPct[tier.key] : 0),
          backgroundColor: tier.color,
          stack: "Client Count"
        }))
      ];
      return datasets;
    }
    // Default color logic
    return [
      {
        label: `Rooms${labelSuffix}`,
        data: percentMetrics.map(m => m.rooms),
        backgroundColor: barColors[0]
      },
      {
        label: `Room Nights${labelSuffix}`,
        data: percentMetrics.map(m => m.rn),
        backgroundColor: barColors[1]
      },
      {
        label: `Revenue${labelSuffix}`,
        data: percentMetrics.map(m => m.revenue),
        backgroundColor: barColors[2]
      },
      {
        label: `Client Count${labelSuffix}`,
        data: percentMetrics.map(m => m.clientCount),
        backgroundColor: barColors[3]
      }
    ];
  }

  let chartData, chartOptions;
  if (showTierBreakdown) {
    // Third view: identical to current year, but all bars orange
    chartData = {
      labels: barLabels,
      datasets: buildDatasets(percentMetricsCurrent, "", true)
    };
    chartOptions = {
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: function(ctx) {
              // Only for Show Tier Breakdown mode
              const { datasetIndex, dataIndex, chart } = ctx;
              const { tierKeys, breakdown } = tierBreakdown;
              // There are 4 metrics, each with 5 tiers (datasets)
              // datasetIndex: 0-4 = Rooms, 5-9 = RN, 10-14 = Revenue, 15-19 = Client Count
              const metricLabels = ["Rooms", "Room Nights", "Revenue", "Client Count"];
              const metricIdx = Math.floor(datasetIndex / 5);
              const tierIdx = datasetIndex % 5;
              const metric = metricLabels[metricIdx];
              const tier = tierKeys[tierIdx].key;
              const manager = chart.data.labels[dataIndex];
              const breakdownData = breakdown[manager];
              let actual = 0;
              let formattedActual = "";
              if (breakdownData) {
                if (metric === "Rooms") actual = breakdownData.tierRooms[tier] || 0;
                else if (metric === "Room Nights") actual = breakdownData.tierRN[tier] || 0;
                else if (metric === "Revenue") actual = breakdownData.tierRevenue[tier] || 0;
                else if (metric === "Client Count") actual = breakdownData.tierCounts[tier] || 0;
                if (metric === "Revenue") {
                  formattedActual = "$" + actual.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                } else {
                  formattedActual = actual.toLocaleString();
                }
              }
              const percent = ctx.parsed.y != null ? ctx.parsed.y.toFixed(1) + "%" : "";
              // Multi-line: Tier type, then metric/actual/percent
              return [`Tier: ${tier}`, `${metric}: ${formattedActual} (${percent})`];
            }
          },
          bodyFont: {
            size: 14,
            family: "Calibri, Arial, sans-serif"
          }
        }
      },
      responsive: true,
      scales: {
        x: {
          stacked: true,
          title: { display: true, text: "Manager" },
          ticks: { font: { size: 14 } }
        },
        y: {
          beginAtZero: true,
          max: 100,
          title: { display: true, text: "Percentage (%)" },
          ticks: { callback: v => `${v}%`, font: { size: 14 } }
        }
      }
    };
  } else if (yearMode === "current") {
    chartData = {
      labels: barLabels,
      datasets: buildDatasets(percentMetricsCurrent, "")
    };
    chartOptions = {
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: { label: ctx => `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(1)}%` },
          bodyFont: {
            size: 14,
            family: "Calibri, Arial, sans-serif"
          }
        }
      },
      responsive: true,
      scales: {
        x: {
          stacked: false,
          title: { display: true, text: "Manager" },
          ticks: { font: { size: 14 } }
        },
        y: {
          beginAtZero: true,
          max: 100,
          title: { display: true, text: "Percentage (%)" },
          ticks: { callback: v => `${v}%`, font: { size: 14 } }
        }
      }
    };
  } else if (yearMode === "last") {
    chartData = {
      labels: barLabels,
      datasets: buildDatasets(percentMetricsLast, " (Last Year)")
    };
    chartOptions = {
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: { label: ctx => `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(1)}%` },
          bodyFont: {
            size: 14,
            family: "Calibri, Arial, sans-serif"
          }
        }
      },
      responsive: true,
      scales: {
        x: {
          stacked: false,
          title: { display: true, text: "Manager" },
          ticks: { font: { size: 14 } }
        },
        y: {
          beginAtZero: true,
          max: 100,
          title: { display: true, text: "Percentage (%)" },
          ticks: { callback: v => `${v}%`, font: { size: 14 } }
        }
      }
    };
  }

  return (
    <div style={MODAL_STYLE}>
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 12,
        marginBottom: 8,
        minHeight: 48
      }}>
        <button
          style={{
            background: "none",
            border: "none",
            color: "#888",
            cursor: "pointer",
            fontSize: "2rem",
            padding: "0 12px",
            display: "flex",
            alignItems: "center"
          }}
          onClick={handleExportExcel}
          title="Export dashboard data to Excel"
          aria-label="Export to Excel"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7,10 12,15 17,10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
        </button>
        <button
          style={{
            ...CLOSE_BTN_STYLE,
            position: "static",
            marginLeft: 8,
            fontSize: "2rem",
            background: "none",
            border: "none",
            color: "#888",
            cursor: "pointer",
            padding: "0 12px"
          }}
          onClick={onClose}
          aria-label="Close"
        >
          &times;
        </button>
      </div>
      <div style={HEADER_STYLE}>Manager Performance Dashboard</div>
      <div style={TOGGLE_STYLE}>
        <label>
          <input
            type="radio"
            name="yearMode"
            value="current"
            checked={showTierBreakdown ? true : yearMode === "current"}
            onChange={() => { setYearMode("current"); setShowTierBreakdown(false); }}
            disabled={showTierBreakdown}
          />
          Current Year ({currentYear})
        </label>
        <label>
          <input
            type="radio"
            name="yearMode"
            value="last"
            checked={showTierBreakdown ? false : yearMode === "last"}
            onChange={() => { setYearMode("last"); setShowTierBreakdown(false); }}
            disabled={showTierBreakdown}
          />
          Last Year ({lastYear})
        </label>
        {/* Overlapping toggle removed as per user instruction */}
        <label>
          <input
            type="checkbox"
            name="showTierBreakdown"
            checked={showTierBreakdown}
            onChange={e => {
              setShowTierBreakdown(e.target.checked);
              if (e.target.checked) setYearMode("current");
            }}
            style={{ marginLeft: 16 }}
          />
          Show Tier Breakdown
        </label>
      </div>
      {/* All legend and breakdown displays removed in tier mode */}
      {!showTierBreakdown && (
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
          <div style={{ display: "flex", gap: 24 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 18, height: 18, background: "rgba(220, 225, 235, 1)", borderRadius: 3, border: "1px solid #b0b8c1" }}></div>
              <span>Rooms</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 18, height: 18, background: "rgba(180, 200, 230, 1)", borderRadius: 3, border: "1px solid #b0b8c1" }}></div>
              <span>Room Nights</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 18, height: 18, background: "rgba(100, 150, 220, 1)", borderRadius: 3, border: "1px solid #b0b8c1" }}></div>
              <span>Revenue</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 18, height: 18, background: "#0074D9", borderRadius: 3, border: "1px solid #b0b8c1" }}></div>
              <span>Client Count</span>
            </div>
          </div>
        </div>
      )}
      {/* Custom legend for tier breakdown mode */}
      {showTierBreakdown && (
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
          <div style={{ display: "flex", gap: 24 }}>
            {tierBreakdown.tierKeys.map(tier => (
              <div key={tier.key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{
                  width: 18,
                  height: 18,
                  background: tier.color,
                  borderRadius: 3,
                  border: "1px solid #b0b8c1"
                }}></div>
                <span>{tier.key}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <Bar
        data={chartData}
        options={{
          ...chartOptions,
          showTierBreakdown, // Pass to plugin via options
          plugins: {
            ...chartOptions.plugins,
            datalabels: {
              display: true,
              color: "#222",
              font: { weight: "bold", size: 14 },
              anchor: "end",
              align: "end",
              formatter: (value) => value ? value.toFixed(1) + "%" : "0%"
            }
          },
          elements: {
            bar: {
              borderRadius: (ctx) => {
                // Only apply custom border radius in Show Tier Breakdown mode (stacked bars)
                if (!showTierBreakdown) return 4;
                // ctx.datasetIndex: index of the dataset (each tier is a dataset)
                // ctx.chart.data.datasets.length / 4 = number of metrics (Rooms, RN, Revenue, Client Count)
                // Each metric has 5 datasets (tiers), so for each metric, the top segment is the last tier with a nonzero value
                // We'll apply radius only to the top segment in each stack
                const { chart, datasetIndex, dataIndex } = ctx;
                const datasets = chart.data.datasets;
                // Find the last visible dataset for this stack (metric)
                // There are 4 metrics, each with 5 tiers (datasets)
                // For each bar group (manager), for each metric, find the topmost nonzero segment
                // Example: datasetIndex 0-4: Rooms, 5-9: RN, 10-14: Revenue, 15-19: Client Count
                const metricSize = 5;
                const metricGroup = Math.floor(datasetIndex / metricSize);
                const metricStart = metricGroup * metricSize;
                const metricEnd = metricStart + metricSize - 1;
                // Find the topmost dataset (from top to bottom: N/A, T3, T2, T1, VIP)
                let topDatasetIdx = null;
                for (let i = metricEnd; i >= metricStart; i--) {
                  if (datasets[i].data[dataIndex] > 0) {
                    topDatasetIdx = i;
                    break;
                  }
                }
                if (topDatasetIdx === datasetIndex) {
                  // This is the top segment
                  return { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 };
                }
                // All other segments: no radius
                return 0;
              },
              borderSkipped: false,
              barPercentage: 0.85,
              categoryPercentage: 0.7
            }
          }
        }}
        plugins={[
          {
            id: "show-bar-percentage-labels",
            afterDatasetsDraw: (chart) => {
              const { ctx, chartArea, options } = chart;
              if (!chartArea) return;
              ctx.save();
              // Use showTierBreakdown from options
              const showTier = options.showTierBreakdown;
              const metricLabels = ["Rooms", "Room Nights", "Revenue", "Client Count"];
              // For each manager (bar group)
              const numManagers = chart.data.labels.length;
              for (let i = 0; i < numManagers; i++) {
                for (let metric = 0; metric < 4; metric++) {
                  if (showTier) {
                    // In tier breakdown mode, find the topmost visible segment for this metric/bar group
                    const metricSize = 5;
                    const metricStart = metric * metricSize;
                    const metricEnd = metricStart + metricSize - 1;
                    let topDatasetIdx = null;
                    for (let d = metricEnd; d >= metricStart; d--) {
                      if (chart.data.datasets[d].data[i] > 0) {
                        topDatasetIdx = d;
                        break;
                      }
                    }
                    if (topDatasetIdx !== null) {
                      const barMeta = chart.getDatasetMeta(topDatasetIdx).data[i];
                      if (barMeta) {
                        // Draw percentage value (copied from non-tier mode)
                        let percentValue = "";
                        if (options.showTierBreakdown && chart.data.labels && chart.data.labels.length === percentMetricsCurrent.length) {
                          // metric: 0=Rooms, 1=Room Nights, 2=Revenue, 3=Client Count
                          const m = percentMetricsCurrent[i];
                          if (m) {
                            if (metric === 0) percentValue = m.rooms;
                            else if (metric === 1) percentValue = m.rn;
                            else if (metric === 2) percentValue = m.revenue;
                            else if (metric === 3) percentValue = m.clientCount;
                          }
                        }
                        ctx.font = "11px Segoe UI, Arial";
                        ctx.fillStyle = "#222";
                        ctx.textAlign = "center";
                        ctx.textBaseline = "bottom";
                        ctx.fillText(
                          (percentValue !== "" ? percentValue.toFixed(1) + "%" : ""),
                          barMeta.x,
                          barMeta.y - 4
                        );
                        // Draw metric label
                        ctx.save();
                        ctx.translate(barMeta.x, barMeta.y - 22 - 100); // 100px above percentage
                        ctx.rotate(-Math.PI / 2);
                        ctx.font = "16px Segoe UI, Arial";
                        ctx.fillStyle = "#0074D9";
                        ctx.textAlign = "right";
                        ctx.textBaseline = "middle";
                        ctx.fillText(metricLabels[metric], 0, 0);
                        ctx.restore();
                      }
                    }
                    continue;
                  }
                  // Non-tier mode: draw percentage and metric label as before
                  const dsIdx = metric;
                  const ds = chart.data.datasets[dsIdx];
                  const value = ds.data[i];
                  const barMeta = chart.getDatasetMeta(dsIdx).data[i];
                  if (typeof value === "number" && !isNaN(value) && barMeta) {
                    // Draw percentage
                    ctx.font = "11px Segoe UI, Arial";
                    ctx.fillStyle = "#222";
                    ctx.textAlign = "center";
                    ctx.textBaseline = "bottom";
                    ctx.fillText(
                      value.toFixed(1) + "%",
                      barMeta.x,
                      barMeta.y - 4
                    );
                    // Draw metric label in normal mode
                    ctx.save();
                    ctx.translate(barMeta.x, barMeta.y - 22 - 100); // 100px above percentage
                    ctx.rotate(-Math.PI / 2);
                    ctx.font = "16px Segoe UI, Arial";
                    ctx.fillStyle = "#0074D9";
                    ctx.textAlign = "right";
                    ctx.textBaseline = "middle";
                    ctx.fillText(metricLabels[metric], 0, 0);
                    ctx.restore();
                  }
                }
              }
              ctx.restore();
            }
          }
        ,
        // Texture on hover plugin
        {
          id: "bar-hover-texture",
          beforeDatasetsDraw: (chart, args, opts) => {
            const active = chart.getActiveElements && chart.getActiveElements();
            if (!active || !active.length) return;
            const { datasetIndex, index } = active[0];
            const meta = chart.getDatasetMeta(datasetIndex);
            const bar = meta.data[index];
            if (!bar) return;
            // Create diagonal stripe pattern (mimic external-client)
            const patternCanvas = document.createElement("canvas");
            patternCanvas.width = 8;
            patternCanvas.height = 8;
            const pctx = patternCanvas.getContext("2d");
            pctx.fillStyle = "#e0f0ff";
            pctx.fillRect(0, 0, 8, 8);
            pctx.strokeStyle = "#b3d9ff";
            pctx.lineWidth = 2;
            pctx.beginPath();
            pctx.moveTo(0, 8);
            pctx.lineTo(8, 0);
            pctx.stroke();
            const pattern = pctx.createPattern(patternCanvas, "repeat");
            // Save original fill
            const origFill = bar.options.backgroundColor;
            // Set pattern fill
            bar.options.backgroundColor = pattern;
            // Redraw just this bar
            bar.draw(chart.ctx);
            // Overlay a semi-transparent dark rectangle to darken
            const { x, y, base, width } = bar.getProps(['x', 'y', 'base', 'width']);
            const barHeight = Math.abs(base - y);
            chart.ctx.save();
            chart.ctx.globalAlpha = 0.18;
            chart.ctx.fillStyle = "#000";
            chart.ctx.fillRect(x - width / 2, Math.min(y, base), width, barHeight);
            chart.ctx.globalAlpha = 1.0;
            chart.ctx.restore();
            // Restore original fill
            bar.options.backgroundColor = origFill;
          }
        }
        ]}
      />
    </div>
  );
}

ManagerPerformanceDashboard.propTypes = {
  clientData: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired
};
